import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Card, Button, Space, Table, Modal, Form, Input, Upload, Tag, Descriptions, App } from 'antd';
import { 
  ArrowLeftOutlined, 
  PlusOutlined, 
  UploadOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileUnknownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { projectAPI } from '../services/api';
import { formatDateTimeBeijing, formatDateBeijing } from '../utils/datetime';

const { Title, Text } = Typography;

const ProjectDetail = () => {
  const { message } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [form] = Form.useForm();

  const loadProject = useCallback(async () => {
    setLoading(true);
    try {
      const response = await projectAPI.get(id);
      setProject(response.data);
    } catch (error) {
      message.error('加载项目失败');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadDocuments = useCallback(async () => {
    try {
      const response = await projectAPI.getDocuments(id);
      setDocuments(response.data);
    } catch (error) {
      message.error('加载文档失败');
    }
  }, [id]);

  useEffect(() => {
    loadProject();
    loadDocuments();
  }, [loadProject, loadDocuments]);

  const getFileIcon = (type) => {
    const icons = {
      'pdf': <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      'docx': <FileTextOutlined style={{ color: '#1890ff' }} />,
      'xlsx': <FileTextOutlined style={{ color: '#52c41a' }} />,
      'zip': <FileTextOutlined style={{ color: '#fa8c16' }} />,
      'dwg': <FileImageOutlined style={{ color: '#722ed1' }} />,
    };
    return icons[type] || <FileUnknownOutlined />;
  };

  const documentColumns = [
    {
      title: '文件名',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (name, record) => (
        <Space>
          {getFileIcon(record.type)}
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: '文件类型',
      dataIndex: 'type',
      key: 'type',
      width: '10%',
      render: (type) => <Tag>{type.toUpperCase()}</Tag>,
    },
    {
      title: '文件大小',
      dataIndex: 'size',
      key: 'size',
      width: '10%',
    },
    {
      title: '上传人',
      dataIndex: 'uploader',
      key: 'uploader',
      width: '12%',
    },
    {
      title: '上传时间',
      dataIndex: 'date',
      key: 'date',
      width: '15%',
    },
    {
      title: '评审状态',
      dataIndex: 'review_status',
      key: 'review_status',
      width: '12%',
      render: (status) => {
        if (!status) return <Tag color="default">未评审</Tag>;
        const statusMap = {
          'pending': { color: 'default', text: '待评审', icon: <ClockCircleOutlined /> },
          'processing': { color: 'processing', text: '评审中', icon: <ClockCircleOutlined /> },
          'completed': { color: 'success', text: '已完成', icon: <CheckCircleOutlined /> },
          'failed': { color: 'error', text: '失败' }
        };
        const { color, text, icon } = statusMap[status] || { color: 'default', text: status, icon: null };
        return <Tag color={color} icon={icon}>{text}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: '21%',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small">下载</Button>
          <Button 
            type="link" 
            size="small"
            onClick={() => navigate(`/documents/${record.id}/review`)}
          >
            评审
          </Button>
          <Button type="link" danger size="small">删除</Button>
        </Space>
      ),
    },
  ];

  const handleUpload = async () => {
    try {
      await form.validateFields();
      message.success('上传成功');
      setUploadModalVisible(false);
      form.resetFields();
      loadDocuments();
    } catch (error) {
      message.error('上传失败');
    }
  };

  if (loading || !project) {
    return <div style={{ padding: '24px' }}>加载中...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/projects')}
          style={{ marginBottom: '16px' }}
        >
          返回项目列表
        </Button>
        <Title level={2} style={{ marginBottom: '8px' }}>{project.name}</Title>
        <Text type="secondary">项目详情与资料管理</Text>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Descriptions 
          title="项目信息" 
          bordered 
          column={2} 
          size="middle"
          labelStyle={{ width: '140px', minWidth: '140px' }}
          contentStyle={{ minWidth: '200px' }}
        >
          <Descriptions.Item label="项目名称">{project.name}</Descriptions.Item>
          <Descriptions.Item label="项目类型">
            <Tag color="blue">{project.type}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="项目分类">
            {project.category ? <Tag>{project.category}</Tag> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="项目状态">
            <Tag color={project.status === 'active' ? 'green' : 'default'}>
              {project.status === 'active' ? '进行中' : '已归档'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="所在省份">{project.province || '-'}</Descriptions.Item>
          <Descriptions.Item label="具体位置">{project.location || '-'}</Descriptions.Item>
          <Descriptions.Item label="项目总投资">{project.total_investment ? `${project.total_investment} 万元` : '-'}</Descriptions.Item>
          <Descriptions.Item label="建设工期">{project.construction_period ? `${project.construction_period} 个月` : '-'}</Descriptions.Item>
          <Descriptions.Item label="占地面积">{project.land_area ? `${project.land_area} 平方米` : '-'}</Descriptions.Item>
          <Descriptions.Item label="土石方总量">{project.earthwork_volume ? `${project.earthwork_volume} 立方米` : '-'}</Descriptions.Item>
          <Descriptions.Item label="计划开工时间">{formatDateBeijing(project.planned_start_date) || '-'}</Descriptions.Item>
          <Descriptions.Item label="计划完工时间">{formatDateBeijing(project.planned_end_date) || '-'}</Descriptions.Item>
          <Descriptions.Item label="建设单位">{project.construction_unit || '-'}</Descriptions.Item>
          <Descriptions.Item label="设计单位">{project.design_unit || '-'}</Descriptions.Item>
          <Descriptions.Item label="施工单位">{project.construction_contractor || '-'}</Descriptions.Item>
          <Descriptions.Item label="监理单位">{project.supervision_unit || '-'}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatDateTimeBeijing(project.created_at)}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{formatDateTimeBeijing(project.updated_at)}</Descriptions.Item>
          <Descriptions.Item label="建设规模" span={2}>{project.construction_scale || '-'}</Descriptions.Item>
          <Descriptions.Item label="项目描述" span={2}>
            {project.description}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>项目资料</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadModalVisible(true)}>
            上传资料
          </Button>
        }
      >
        <Table
          columns={documentColumns}
          dataSource={documents}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个文件`,
          }}
        />
      </Card>

      <Modal
        title="上传资料"
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={() => setUploadModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="file"
            label="选择文件"
            rules={[{ required: true, message: '请选择文件' }]}
          >
            <Upload.Dragger>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">支持单个或批量上传</p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item
            name="description"
            label="文件描述"
          >
            <Input.TextArea
              placeholder="请输入文件描述"
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectDetail;
